import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Lock, Eye, FileText, Globe, UserCheck, Database, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: January 2026",
      intro: "At Adidoo, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services, in compliance with Saudi Arabian regulations including the Personal Data Protection Law (PDPL).",
      sections: [
        {
          icon: Database,
          title: "1. Information We Collect",
          content: [
            {
              subtitle: "Personal Information:",
              items: [
                "Name, email address, phone number",
                "Business information (company name, position)",
                "Billing and payment information",
                "Saudi national ID or Iqama number (when required for services)",
                "Communication preferences and history"
              ]
            },
            {
              subtitle: "Technical Information:",
              items: [
                "IP address and device information",
                "Browser type and version",
                "Usage data and analytics",
                "Cookies and tracking technologies"
              ]
            }
          ]
        },
        {
          icon: Eye,
          title: "2. How We Use Your Information",
          content: [
            {
              subtitle: "We use your information to:",
              items: [
                "Provide and maintain our services",
                "Process transactions and send invoices",
                "Communicate with you about services and updates",
                "Improve our services and user experience",
                "Comply with legal obligations under Saudi law",
                "Prevent fraud and ensure security",
                "Send marketing communications (with your consent)"
              ]
            }
          ]
        },
        {
          icon: Lock,
          title: "3. Data Protection and Security",
          content: [
            {
              subtitle: "Security Measures:",
              items: [
                "SSL/TLS encryption for data transmission",
                "Secure data storage with encryption at rest",
                "Regular security audits and assessments",
                "Access controls and authentication",
                "Employee training on data protection",
                "Compliance with Saudi Cybersecurity Framework"
              ]
            }
          ]
        },
        {
          icon: Globe,
          title: "4. Data Storage and Transfer",
          content: [
            {
              subtitle: "",
              items: [
                "Primary data storage within Saudi Arabia or approved jurisdictions",
                "Data transfers comply with PDPL requirements",
                "Third-party processors are vetted for security compliance",
                "Cross-border transfers only with adequate safeguards",
                "Cloud services meet Saudi regulatory standards"
              ]
            }
          ]
        },
        {
          icon: UserCheck,
          title: "5. Your Rights Under Saudi Law",
          content: [
            {
              subtitle: "You have the right to:",
              items: [
                "Access your personal data",
                "Request correction of inaccurate data",
                "Request deletion of your data (subject to legal requirements)",
                "Object to processing of your data",
                "Withdraw consent for marketing communications",
                "Lodge a complaint with the Saudi Data & AI Authority (SDAIA)",
                "Request data portability"
              ]
            }
          ]
        },
        {
          icon: FileText,
          title: "6. Data Retention",
          content: [
            {
              subtitle: "",
              items: [
                "Personal data retained as long as necessary for services",
                "Financial records kept for 10 years (Saudi tax law)",
                "Marketing data deleted upon withdrawal of consent",
                "Account data retained for 1 year after account closure",
                "Legal compliance may require longer retention periods"
              ]
            }
          ]
        },
        {
          icon: AlertCircle,
          title: "7. Cookies and Tracking",
          content: [
            {
              subtitle: "We use cookies for:",
              items: [
                "Essential site functionality",
                "Analytics and performance monitoring",
                "Personalization and preferences",
                "Marketing and advertising (with consent)"
              ]
            },
            {
              subtitle: "",
              items: [
                "You can control cookies through browser settings. Disabling cookies may affect site functionality."
              ]
            }
          ]
        },
        {
          icon: Shield,
          title: "8. Third-Party Services",
          content: [
            {
              subtitle: "We may share data with:",
              items: [
                "Payment processors (secure, PCI-DSS compliant)",
                "Cloud service providers",
                "Analytics services",
                "Marketing platforms (with consent)",
                "Legal and regulatory authorities when required"
              ]
            },
            {
              subtitle: "",
              items: [
                "All third parties are contractually bound to protect your data and comply with applicable laws."
              ]
            }
          ]
        }
      ],
      compliance: {
        title: "Compliance with Saudi Regulations",
        items: [
          "Personal Data Protection Law (PDPL)",
          "Saudi Data & AI Authority (SDAIA) guidelines",
          "National Cybersecurity Authority (NCA) standards",
          "E-Commerce Law",
          "Anti-Cyber Crime Law"
        ]
      },
      contact: {
        title: "Contact Us",
        text: "For privacy-related inquiries or to exercise your rights:",
        email: "privacy@adidoo.com",
        phone: "+966 55 182 6405",
        address: "Data Protection Officer, Adidoo, Riyadh, Saudi Arabia"
      }
    },
    ar: {
      title: "سياسة الخصوصية",
      lastUpdated: "آخر تحديث: يناير 2026",
      intro: "في أديدو، نلتزم بحماية خصوصيتك وضمان أمن معلوماتك الشخصية. توضح سياسة الخصوصية هذه كيفية جمع معلوماتك واستخدامها والإفصاح عنها وحمايتها عند استخدام خدماتنا، بما يتوافق مع اللوائح السعودية بما في ذلك نظام حماية البيانات الشخصية.",
      sections: [
        {
          icon: Database,
          title: "1. المعلومات التي نجمعها",
          content: [
            {
              subtitle: "المعلومات الشخصية:",
              items: [
                "الاسم، عنوان البريد الإلكتروني، رقم الهاتف",
                "معلومات العمل (اسم الشركة، المنصب)",
                "معلومات الفوترة والدفع",
                "رقم الهوية الوطنية أو الإقامة السعودية (عند الحاجة للخدمات)",
                "تفضيلات التواصل وسجله"
              ]
            },
            {
              subtitle: "المعلومات التقنية:",
              items: [
                "عنوان IP ومعلومات الجهاز",
                "نوع المتصفح وإصداره",
                "بيانات الاستخدام والتحليلات",
                "ملفات تعريف الارتباط وتقنيات التتبع"
              ]
            }
          ]
        },
        {
          icon: Eye,
          title: "2. كيف نستخدم معلوماتك",
          content: [
            {
              subtitle: "نستخدم معلوماتك من أجل:",
              items: [
                "تقديم خدماتنا والحفاظ عليها",
                "معالجة المعاملات وإرسال الفواتير",
                "التواصل معك حول الخدمات والتحديثات",
                "تحسين خدماتنا وتجربة المستخدم",
                "الامتثال للالتزامات القانونية بموجب القانون السعودي",
                "منع الاحتيال وضمان الأمن",
                "إرسال اتصالات تسويقية (بموافقتك)"
              ]
            }
          ]
        },
        {
          icon: Lock,
          title: "3. حماية البيانات والأمان",
          content: [
            {
              subtitle: "الإجراءات الأمنية:",
              items: [
                "تشفير SSL/TLS لنقل البيانات",
                "تخزين آمن للبيانات مع التشفير أثناء السكون",
                "عمليات تدقيق وتقييم أمنية منتظمة",
                "ضوابط الوصول والمصادقة",
                "تدريب الموظفين على حماية البيانات",
                "الامتثال للإطار السيبراني السعودي"
              ]
            }
          ]
        },
        {
          icon: Globe,
          title: "4. تخزين ونقل البيانات",
          content: [
            {
              subtitle: "",
              items: [
                "التخزين الأساسي للبيانات داخل المملكة أو الولايات القضائية المعتمدة",
                "عمليات نقل البيانات متوافقة مع متطلبات نظام حماية البيانات",
                "يتم فحص معالجي الطرف الثالث للامتثال الأمني",
                "التحويلات عبر الحدود فقط مع ضمانات كافية",
                "خدمات السحابة تلبي المعايير التنظيمية السعودية"
              ]
            }
          ]
        },
        {
          icon: UserCheck,
          title: "5. حقوقك بموجب القانون السعودي",
          content: [
            {
              subtitle: "لديك الحق في:",
              items: [
                "الوصول إلى بياناتك الشخصية",
                "طلب تصحيح البيانات غير الدقيقة",
                "طلب حذف بياناتك (مع مراعاة المتطلبات القانونية)",
                "الاعتراض على معالجة بياناتك",
                "سحب الموافقة على الاتصالات التسويقية",
                "تقديم شكوى إلى الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)",
                "طلب نقل البيانات"
              ]
            }
          ]
        },
        {
          icon: FileText,
          title: "6. الاحتفاظ بالبيانات",
          content: [
            {
              subtitle: "",
              items: [
                "الاحتفاظ بالبيانات الشخصية طالما كان ذلك ضروريًا للخدمات",
                "الاحتفاظ بالسجلات المالية لمدة 10 سنوات (قانون الضرائب السعودي)",
                "حذف بيانات التسويق عند سحب الموافقة",
                "الاحتفاظ ببيانات الحساب لمدة سنة بعد إغلاق الحساب",
                "قد يتطلب الامتثال القانوني فترات احتفاظ أطول"
              ]
            }
          ]
        },
        {
          icon: AlertCircle,
          title: "7. ملفات تعريف الارتباط والتتبع",
          content: [
            {
              subtitle: "نستخدم ملفات تعريف الارتباط لـ:",
              items: [
                "وظائف الموقع الأساسية",
                "مراقبة التحليلات والأداء",
                "التخصيص والتفضيلات",
                "التسويق والإعلان (بموافقتك)"
              ]
            },
            {
              subtitle: "",
              items: [
                "يمكنك التحكم في ملفات تعريف الارتباط من خلال إعدادات المتصفح. قد يؤثر تعطيل ملفات تعريف الارتباط على وظائف الموقع."
              ]
            }
          ]
        },
        {
          icon: Shield,
          title: "8. خدمات الطرف الثالث",
          content: [
            {
              subtitle: "قد نشارك البيانات مع:",
              items: [
                "معالجي الدفع (آمنة، متوافقة مع PCI-DSS)",
                "مزودي الخدمات السحابية",
                "خدمات التحليلات",
                "منصات التسويق (بموافقتك)",
                "السلطات القانونية والتنظيمية عند الحاجة"
              ]
            },
            {
              subtitle: "",
              items: [
                "جميع الأطراف الثالثة ملزمة تعاقديًا بحماية بياناتك والامتثال للقوانين المعمول بها."
              ]
            }
          ]
        }
      ],
      compliance: {
        title: "الامتثال للوائح السعودية",
        items: [
          "نظام حماية البيانات الشخصية",
          "إرشادات الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)",
          "معايير الهيئة الوطنية للأمن السيبراني",
          "نظام التجارة الإلكترونية",
          "نظام مكافحة جرائم المعلوماتية"
        ]
      },
      contact: {
        title: "اتصل بنا",
        text: "للاستفسارات المتعلقة بالخصوصية أو لممارسة حقوقك:",
        email: "privacy@adidoo.com",
        phone: "+966 55 182 6405",
        address: "مسؤول حماية البيانات، أديدو، الرياض، المملكة العربية السعودية"
      }
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-20 h-20 text-white mx-auto mb-6 animate-pulse" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            {currentContent.title}
          </h1>
          <p className="text-blue-100 text-lg">{currentContent.lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-l-4 border-blue-600">
          <p className="text-gray-700 leading-relaxed text-lg" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.intro}
          </p>
        </div>

        {/* Sections */}
        {currentContent.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg p-8 mb-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <section.icon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 pt-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.title}
              </h2>
            </div>
            {section.content.map((subsection, subIndex) => (
              <div key={subIndex} className="mb-6 last:mb-0" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {subsection.subtitle && (
                  <h3 className="font-semibold text-gray-800 mb-3 text-lg">
                    {subsection.subtitle}
                  </h3>
                )}
                <ul className="space-y-2">
                  {subsection.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}

        {/* Compliance */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 mb-8 border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <FileText className="w-8 h-8 text-green-600" />
            {currentContent.compliance.title}
          </h2>
          <ul className="space-y-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.compliance.items.map((item, index) => (
              <li key={index} className="flex items-center gap-3">
                <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                <span className="text-gray-800 font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.title}
          </h2>
          <p className="mb-6 text-blue-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.text}
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5" />
              <a href={`mailto:${currentContent.contact.email}`} className="hover:underline" dir="ltr">
                {currentContent.contact.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5" />
              <a href={`tel:${currentContent.contact.phone}`} className="hover:underline" dir="ltr">
                {currentContent.contact.phone}
              </a>
            </div>
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 mt-1 flex-shrink-0" />
              <span dir={language === 'ar' ? 'rtl' : 'ltr'}>{currentContent.contact.address}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;